import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3-force';
import * as sel from 'd3-selection';
import * as zoom from 'd3-zoom';
import * as drag from 'd3-drag';

const NODE_LIMIT = 200;

interface GNode { id: string; label: string; props: Record<string, any>; }
interface GEdge { id: string; source: string; target: string; type: string; props: Record<string, any>; }

// ── Detect this API's node/rel format ─────────────────────────────────────
// Node:  { type: "node", id: "4:...", labels: [...], properties: {...} }
// Rel:   { type: "relationship", rel_type: "...", start_node_id: "...", end_node_id: "...", id: "..." }
function isApiNode(v: any): boolean {
  return v !== null && typeof v === 'object' && v.type === 'node' && Array.isArray(v.labels);
}
function isApiRel(v: any): boolean {
  return v !== null && typeof v === 'object' && v.type === 'relationship' && ('start_node_id' in v || 'startNodeElementId' in v);
}

// Also handle bolt/driver format:
// Node:  { labels: [...], properties: {...}, elementId: "..." }  (no .type field)
// Rel:   { type: "...", startNodeElementId: "...", endNodeElementId: "..." }
function isBoltNode(v: any): boolean {
  return v !== null && typeof v === 'object' && !('type' in v) && Array.isArray(v.labels) && 'properties' in v;
}
function isBoltRel(v: any): boolean {
  return v !== null && typeof v === 'object' && typeof v.type === 'string' && v.type !== 'node' && v.type !== 'relationship' &&
    ('startNodeElementId' in v || 'start' in v);
}

function extractGraph(rows: any[]): { nodes: GNode[]; edges: GEdge[]; tooMany: boolean } {
  const nodeMap = new Map<string, GNode>();
  const edgeMap = new Map<string, GEdge>();

  function addNode(v: any): string {
    const id = String(v.id ?? v.elementId ?? JSON.stringify(v));
    if (!nodeMap.has(id)) {
      const labels: string[] = v.labels ?? (v.label ? [v.label] : ['Node']);
      nodeMap.set(id, { id, label: labels[0] ?? 'Node', props: v.properties ?? {} });
    }
    return id;
  }

  function addRel(v: any): void {
    // API format uses start_node_id / end_node_id; bolt uses startNodeElementId / endNodeElementId
    const src = String(v.start_node_id ?? v.startNodeElementId ?? v.start ?? '');
    const tgt = String(v.end_node_id   ?? v.endNodeElementId   ?? v.end   ?? '');
    if (!src || !tgt) return;
    const id  = String(v.id ?? v.elementId ?? `${src}→${tgt}`);
    if (!edgeMap.has(id)) {
      const relType = v.rel_type ?? (v.type !== 'relationship' ? v.type : '') ?? '';
      edgeMap.set(id, { id, source: src, target: tgt, type: relType, props: v.properties ?? {} });
    }
  }

  function walk(v: any): void {
    if (v === null || v === undefined || typeof v !== 'object') return;
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (isApiNode(v))  { addNode(v); return; }
    if (isApiRel(v))   { addRel(v);  return; }
    if (isBoltNode(v)) { addNode(v); return; }
    if (isBoltRel(v))  { addRel(v);  return; }
    // Recurse into plain objects (e.g. the row object { n: node, r: rel, m: node })
    Object.values(v).forEach(walk);
  }

  rows.forEach(walk);

  const validEdges = Array.from(edgeMap.values()).filter(
    e => nodeMap.has(e.source) && nodeMap.has(e.target)
  );

  const nodes = Array.from(nodeMap.values());
  const tooMany = nodes.length > NODE_LIMIT;
  return { nodes: tooMany ? [] : nodes, edges: tooMany ? [] : validEdges, tooMany };
}

// ── Colour palette per label ───────────────────────────────────────────────
const PALETTE = [
  '#7c3aed','#2563eb','#0891b2','#16a34a','#d97706','#dc2626',
  '#7e22ce','#1d4ed8','#0e7490','#15803d','#b45309','#b91c1c',
];

interface GraphPlotProps { rows: any[]; }

const GraphPlot: React.FC<GraphPlotProps> = ({ rows }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const { nodes, edges, tooMany } = useMemo(() => extractGraph(rows), [rows]);

  // Per-instance color map — reset whenever nodes change to avoid stale color assignments
  const labelColorMap = useMemo(() => new Map<string, string>(), [nodes]); // eslint-disable-line react-hooks/exhaustive-deps
  let paletteIdx = 0;
  function labelColor(label: string): string {
    if (!labelColorMap.has(label)) labelColorMap.set(label, PALETTE[paletteIdx++ % PALETTE.length]);
    return labelColorMap.get(label)!;
  }

  useEffect(() => {
    if (tooMany || !nodes.length || !svgRef.current) return;

    // Wait for layout paint so clientWidth/Height are real
    const el = svgRef.current;
    let width  = el.clientWidth  || el.getBoundingClientRect().width  || 600;
    let height = el.clientHeight || el.getBoundingClientRect().height || 400;

    // If still zero, defer one frame
    const run = () => {
      width  = el.clientWidth  || el.getBoundingClientRect().width  || 600;
      height = el.clientHeight || el.getBoundingClientRect().height || 400;
      initGraph(width, height);
    };

    if (width < 10 || height < 10) {
      const raf = requestAnimationFrame(run);
      return () => cancelAnimationFrame(raf);
    }

    const cleanup = initGraph(width, height);
    return cleanup;

    function initGraph(width: number, height: number): () => void {
    const svg = sel.select(el);
    svg.selectAll('*').remove();

    // Zoom layer
    const g = svg.append('g');
    svg.call(
      (zoom.zoom() as any).scaleExtent([0.1, 6]).on('zoom', (event: any) => {
        g.attr('transform', event.transform);
      })
    );

    // Arrow marker
    svg.append('defs').append('marker')
      .attr('id', 'gp-arrow')
      .attr('viewBox', '0 -4 8 8')
      .attr('refX', 22).attr('refY', 0)
      .attr('markerWidth', 6).attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path').attr('d', 'M0,-4L8,0L0,4').attr('fill', '#94a3b8');

    // Build sim copies
    const simNodes: any[] = nodes.map(n => ({ ...n }));
    const idToSim = new Map(simNodes.map(n => [n.id, n]));
    const simEdges: any[] = edges.map(e => ({
      ...e,
      source: idToSim.get(e.source) ?? e.source,
      target: idToSim.get(e.target) ?? e.target,
    }));

    const sim = d3.forceSimulation(simNodes)
      .force('link',      d3.forceLink(simEdges).id((d: any) => d.id).distance(110).strength(0.6))
      .force('charge',    d3.forceManyBody().strength(nodes.length > 50 ? -120 : -250))
      .force('center',    d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide(26));

    function escHtml(s: string): string {
      return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function truncate(v: any, max = 60): string {
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v ?? '');
      return s.length > max ? s.slice(0, max) + '…' : s;
    }

    function propLines(obj: Record<string, any>): string {
      return Object.entries(obj)
        .slice(0, 10)
        .map(([k, v]) => `<span style="color:#94a3b8">${escHtml(k)}:</span> ${escHtml(truncate(v))}`)
        .join('\n');
    }

    // Shared tooltip
    const tip = sel.select('body').append('div')
      .style('position', 'fixed')
      .style('background', '#1e293b')
      .style('color', '#e2e8f0')
      .style('font-size', '11px')
      .style('font-family', 'var(--font-mono)')
      .style('padding', '6px 10px')
      .style('border-radius', '6px')
      .style('pointer-events', 'none')
      .style('opacity', '0')
      .style('z-index', '9999')
      .style('max-width', '260px')
      .style('width', 'max-content')
      .style('white-space', 'pre-wrap')
      .style('word-break', 'break-word')
      .style('overflow-wrap', 'break-word')
      .style('line-height', '1.5')
      .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)');

    function bindTooltip(selection: any, getHtml: (d: any) => string) {
      selection
        .on('mouseenter', (_ev: any, d: any) => tip.style('opacity', '1').html(getHtml(d)))
        .on('mousemove',  (ev: any)            => tip.style('left', (ev.clientX + 14) + 'px').style('top', (ev.clientY - 8) + 'px'))
        .on('mouseleave', ()                   => tip.style('opacity', '0'));
    }

    // Edges
    const link = g.append('g').attr('stroke', '#cbd5e1').selectAll('line')
      .data(simEdges).join('line')
      .attr('stroke-width', 1.5)
      .attr('marker-end', 'url(#gp-arrow)')
      .attr('cursor', 'pointer');

    bindTooltip(link, (d: any) => {
      const lines = propLines(d.props ?? {});
      return `<strong style="color:#67e8f9">${escHtml(d.type || 'edge')}</strong>` + (lines ? `\n${lines}` : '');
    });

    // Edge type labels (only when ≤ 80 edges to avoid clutter)
    const edgeLabel = edges.length <= 80
      ? g.append('g').selectAll('text')
          .data(simEdges).join('text')
          .attr('font-size', 8)
          .attr('fill', '#94a3b8')
          .attr('text-anchor', 'middle')
          .attr('pointer-events', 'none')
          .text((d: any) => d.type)
      : null;

    // Node groups
    const node = g.append('g').selectAll('g')
      .data(simNodes).join('g')
      .attr('cursor', 'grab')
      .call(
        (drag.drag() as any)
          .on('start', (ev: any, d: any) => { if (!ev.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
          .on('drag',  (ev: any, d: any) => { d.fx = ev.x; d.fy = ev.y; })
          .on('end',   (ev: any, d: any) => { if (!ev.active) sim.alphaTarget(0); d.fx = null; d.fy = null; })
      );

    node.append('circle')
      .attr('r', 18)
      .attr('fill', (d: any) => labelColor(d.label))
      .attr('fill-opacity', 0.88)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', 8)
      .attr('fill', '#fff')
      .attr('font-weight', '700')
      .attr('pointer-events', 'none')
      .text((d: any) => d.label.length > 9 ? d.label.slice(0, 8) + '…' : d.label);

    bindTooltip(node, (d: any) => {
      const lines = propLines(d.props ?? {});
      return `<strong style="color:#a78bfa">${escHtml(d.label)}</strong>` + (lines ? `\n${lines}` : '');
    });

    sim.on('tick', () => {
      link
        .attr('x1', (d: any) => d.source.x).attr('y1', (d: any) => d.source.y)
        .attr('x2', (d: any) => d.target.x).attr('y2', (d: any) => d.target.y);
      if (edgeLabel) {
        edgeLabel
          .attr('x', (d: any) => (d.source.x + d.target.x) / 2)
          .attr('y', (d: any) => (d.source.y + d.target.y) / 2);
      }
      node.attr('transform', (d: any) => `translate(${d.x},${d.y})`);
    });

    return () => { sim.stop(); tip.remove(); };
    } // end initGraph
  }, [nodes, edges, tooMany]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── States ────────────────────────────────────────────────────────────────

  if (tooMany) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '40px 20px' }}>
        <div style={{ fontSize: 32, color: '#d1d5db' }}>⬡</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--foreground)' }}>Too many nodes to plot</div>
        <div style={{ fontSize: 13, color: 'var(--muted-foreground)', textAlign: 'center', maxWidth: 320, lineHeight: 1.5 }}>
          Result has more than {NODE_LIMIT} nodes. Use <strong>Graph Explorer</strong> for large-scale visualisation.
        </div>
      </div>
    );
  }

  if (!nodes.length) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '40px 20px' }}>
        <div style={{ fontSize: 28, color: '#d1d5db' }}>⬡</div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--foreground)' }}>No graph data</div>
        <div style={{ fontSize: 13, color: 'var(--muted-foreground)', textAlign: 'center', maxWidth: 320, lineHeight: 1.5 }}>
          Query results don't contain node or relationship objects.{' '}
          Run a pattern query like{' '}
          <code style={{ fontSize: 11, background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }}>
            MATCH (n)-[r]-&gt;(m) RETURN n,r,m LIMIT 50
          </code>
        </div>
      </div>
    );
  }

  const uniqueLabels = Array.from(new Set(nodes.map(n => n.label)));

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
      {/* Legend bar */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 14px',
        padding: '5px 12px', borderBottom: '1px solid #f3f4f6', flexShrink: 0,
        fontSize: 11,
      }}>
        {uniqueLabels.map(label => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--foreground)' }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: labelColor(label), flexShrink: 0 }} />
            {label}
          </span>
        ))}
        <span style={{ marginLeft: 'auto', color: 'var(--muted-foreground)', whiteSpace: 'nowrap' }}>
          {nodes.length} nodes · {edges.length} edges · scroll to zoom · drag to pan
        </span>
      </div>
      <svg ref={svgRef} style={{ flex: 1, width: '100%', height: '100%', minHeight: 0, display: 'block' }} />
    </div>
  );
};

export default GraphPlot;
